import React, { useEffect, useState } from 'react';
import {
  Server, Loader2, RefreshCw, Search, ArrowLeft, HardDrive,
  Cpu, MemoryStick, Users, Clock, Globe, ChevronRight,
  Play, Square, RotateCcw, Skull, ShieldBan, ShieldCheck,
  ExternalLink, AlertTriangle,
} from 'lucide-react';
import api from '../../lib/api';
import ServerManagement from '../hosting/ServerManagement';

interface HostedServer {
  id: string;
  name: string;
  owner_id: string;
  owner_username: string;
  owner_avatar: string | null;
  plan_name: string | null;
  price_monthly: number | null;
  server_type: string;
  mc_version: string;
  subdomain: string;
  status: string;
  memory_mb: number;
  disk_mb: number;
  cpu_limit: number;
  allocation_ip: string | null;
  allocation_port: number | null;
  max_players: number;
  description: string | null;
  created_at: string;
  last_active_at: string | null;
  erozion_server_name: string | null;
  backup_count?: number;
  last_usage?: { cpu_percent: number; memory_mb: number; disk_mb: number; player_count: number } | null;
}

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  online: { label: 'En ligne', cls: 'bg-success/10 text-success' },
  offline: { label: 'Hors ligne', cls: 'bg-text-muted/10 text-text-muted' },
  starting: { label: 'Démarrage', cls: 'bg-warning/10 text-warning' },
  stopping: { label: 'Arrêt', cls: 'bg-warning/10 text-warning' },
  installing: { label: 'Installation', cls: 'bg-info/10 text-info' },
  suspended: { label: 'Suspendu', cls: 'bg-error/10 text-error' },
  hibernated: { label: 'Hiberné', cls: 'bg-text-muted/10 text-text-muted' },
};

const AdminErozionGo: React.FC = () => {
  const [servers, setServers] = useState<HostedServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<HostedServer | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [managingId, setManagingId] = useState<string | null>(null);

  const fetchServers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/erozion-go/servers');
      setServers(data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const fetchDetail = async (id: string) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/erozion-go/servers/${id}`);
      setDetail(data);
    } catch { /* silent */ }
    setLoadingDetail(false);
  };

  useEffect(() => { fetchServers(); }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId]);

  const sendPower = async (action: string) => {
    if (!selectedId) return;
    setActionLoading(action);
    try {
      await api.post(`/admin/erozion-go/servers/${selectedId}/power`, { action });
      await fetchDetail(selectedId);
    } catch { /* silent */ }
    setActionLoading(null);
  };

  const toggleSuspend = async () => {
    if (!selectedId) return;
    setActionLoading('suspend');
    try {
      await api.post(`/admin/erozion-go/servers/${selectedId}/suspend`);
      await fetchDetail(selectedId);
      setConfirmSuspend(false);
    } catch { /* silent */ }
    setActionLoading(null);
  };

  const openServerPanel = () => {
    if (detail) setManagingId(detail.id);
  };

  const filtered = servers.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.owner_username.toLowerCase().includes(search.toLowerCase()) ||
    s.subdomain.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Server Management panel (reuses user's ServerManagement) ── */
  if (managingId) {
    return (
      <ServerManagement
        serverId={managingId}
        onBack={() => setManagingId(null)}
      />
    );
  }

  /* ── Detail view ── */
  if (selectedId && detail) {
    const st = STATUS_STYLES[detail.status] || { label: detail.status, cls: 'bg-bg-tertiary text-text-secondary' };
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-5">
          {/* Back */}
          <button onClick={() => { setSelectedId(null); setDetail(null); }} className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Retour à la liste
          </button>

          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Server className="h-6 w-6 text-green-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-text-primary">{detail.name}</h2>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
              </div>
              <p className="text-xs text-text-muted mt-0.5">
                {detail.subdomain}.erozion.go · {detail.server_type} {detail.mc_version}
                {detail.plan_name && ` · ${detail.plan_name}`}
              </p>
            </div>
            <button
              onClick={openServerPanel}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-admin-accent/10 text-admin-accent hover:bg-admin-accent/20 text-sm font-medium transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Ouvrir le panel
            </button>
          </div>

          {/* ── Actions toolbar ── */}
          <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mr-2">Actions</span>

              {detail.status !== 'suspended' && (
                <>
                  {(detail.status === 'offline' || detail.status === 'stopping') && (
                    <button
                      onClick={() => sendPower('start')}
                      disabled={!!actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/10 text-success hover:bg-success/20 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'start' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                      Démarrer
                    </button>
                  )}
                  {(detail.status === 'online' || detail.status === 'starting') && (
                    <button
                      onClick={() => sendPower('stop')}
                      disabled={!!actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-warning/10 text-warning hover:bg-warning/20 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'stop' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Square className="h-3 w-3" />}
                      Arrêter
                    </button>
                  )}
                  {detail.status === 'online' && (
                    <button
                      onClick={() => sendPower('restart')}
                      disabled={!!actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-info/10 text-info hover:bg-info/20 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'restart' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                      Redémarrer
                    </button>
                  )}
                  <button
                    onClick={() => sendPower('kill')}
                    disabled={!!actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-error/10 text-error hover:bg-error/20 text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'kill' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Skull className="h-3 w-3" />}
                    Kill
                  </button>
                </>
              )}

              <div className="flex-1" />

              {/* Suspend/Unsuspend */}
              {!confirmSuspend ? (
                <button
                  onClick={() => detail.status === 'suspended' ? toggleSuspend() : setConfirmSuspend(true)}
                  disabled={!!actionLoading}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                    detail.status === 'suspended'
                      ? 'bg-success/10 text-success hover:bg-success/20'
                      : 'bg-error/10 text-error hover:bg-error/20'
                  }`}
                >
                  {actionLoading === 'suspend' ? <Loader2 className="h-3 w-3 animate-spin" /> : detail.status === 'suspended' ? <ShieldCheck className="h-3 w-3" /> : <ShieldBan className="h-3 w-3" />}
                  {detail.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-error/5 border border-error/20 rounded-lg px-3 py-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-error" />
                  <span className="text-xs text-error">Confirmer ?</span>
                  <button onClick={toggleSuspend} disabled={!!actionLoading} className="px-2 py-0.5 rounded bg-error text-white text-xs font-medium hover:bg-error/80 disabled:opacity-50">
                    {actionLoading === 'suspend' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Oui'}
                  </button>
                  <button onClick={() => setConfirmSuspend(false)} className="px-2 py-0.5 rounded bg-bg-tertiary text-text-muted text-xs hover:text-text-primary">Non</button>
                </div>
              )}
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { icon: Cpu, label: 'CPU', value: `${detail.cpu_limit}%` },
              { icon: MemoryStick, label: 'RAM', value: `${detail.memory_mb} MB` },
              { icon: HardDrive, label: 'Disque', value: `${detail.disk_mb} MB` },
              { icon: Users, label: 'Max joueurs', value: String(detail.max_players) },
            ].map((s) => (
              <div key={s.label} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3">
                <div className="flex items-center gap-1.5 text-xs text-text-muted mb-1"><s.icon className="h-3.5 w-3.5" />{s.label}</div>
                <p className="text-sm font-semibold text-text-primary">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Live usage */}
          {detail.last_usage && (
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-3">Utilisation actuelle</p>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-text-muted">CPU</p>
                  <p className="text-sm font-semibold text-text-primary">{detail.last_usage.cpu_percent}%</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">RAM</p>
                  <p className="text-sm font-semibold text-text-primary">{detail.last_usage.memory_mb} MB</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Disque</p>
                  <p className="text-sm font-semibold text-text-primary">{detail.last_usage.disk_mb} MB</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted">Joueurs</p>
                  <p className="text-sm font-semibold text-text-primary">{detail.last_usage.player_count}</p>
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4 space-y-3">
              <p className="text-xs text-text-muted uppercase tracking-wider">Propriétaire</p>
              <div className="flex items-center gap-2">
                {detail.owner_avatar ? (
                  <img src={detail.owner_avatar} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-admin-accent/10 flex items-center justify-center text-xs font-bold text-admin-accent">
                    {detail.owner_username[0].toUpperCase()}
                  </div>
                )}
                <span className="text-sm text-text-primary font-medium">@{detail.owner_username}</span>
              </div>
            </div>
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4 space-y-3">
              <p className="text-xs text-text-muted uppercase tracking-wider">Détails</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-text-muted">Créé</span><span className="text-text-secondary">{new Date(detail.created_at).toLocaleDateString('fr-CA')}</span></div>
                {detail.last_active_at && <div className="flex justify-between"><span className="text-text-muted">Dernière activité</span><span className="text-text-secondary">{new Date(detail.last_active_at).toLocaleDateString('fr-CA')}</span></div>}
                {detail.allocation_ip && <div className="flex justify-between"><span className="text-text-muted">IP</span><span className="text-text-secondary font-mono">{detail.allocation_ip}:{detail.allocation_port}</span></div>}
                <div className="flex justify-between"><span className="text-text-muted">Sauvegardes</span><span className="text-text-secondary">{detail.backup_count ?? 0}</span></div>
                {detail.price_monthly && <div className="flex justify-between"><span className="text-text-muted">Prix</span><span className="text-text-secondary">{Number(detail.price_monthly).toFixed(2)} $/mois</span></div>}
              </div>
            </div>
          </div>

          {detail.description && (
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Description</p>
              <p className="text-sm text-text-secondary">{detail.description}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── List view ── */
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Erozion Go</h2>
            <p className="text-xs text-text-muted mt-0.5">Serveurs Minecraft hébergés sur la plateforme</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchServers} className="p-2 rounded-lg hover:bg-highlight text-text-muted"><RefreshCw className="h-3.5 w-3.5" /></button>
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-admin-accent/50"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-admin-accent" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Server className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucun serveur hébergé</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((s) => {
              const st = STATUS_STYLES[s.status] || { label: s.status, cls: 'bg-bg-tertiary text-text-secondary' };
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className="w-full flex items-center gap-3 bg-bg-secondary/40 rounded-xl px-4 py-3 hover:bg-bg-secondary/60 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Server className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary truncate">{s.name}</span>
                      <span className="text-[10px] bg-bg-tertiary text-text-secondary px-1.5 py-0.5 rounded">{s.server_type} {s.mc_version}</span>
                      {s.plan_name && <span className="text-[10px] bg-bg-tertiary text-text-secondary px-1.5 py-0.5 rounded">{s.plan_name}</span>}
                    </div>
                    <p className="text-xs text-text-muted">@{s.owner_username} · {s.subdomain}.erozion.go · {new Date(s.created_at).toLocaleDateString('fr-CA')}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                  <ChevronRight className="h-4 w-4 text-text-muted/40" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminErozionGo;
